import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ExternalLink, ArrowLeft, Share2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RenderView = () => {
  const { renderId } = useParams();
  const [render, setRender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRender();
  }, [renderId]);

  const fetchRender = async () => {
    try {
      // Support both regular renders and sample preview
      const endpoint = renderId === 'preview-sample' 
        ? `${BACKEND_URL}/api/render/preview/sample`
        : `${BACKEND_URL}/api/render/${renderId}`;
      const response = await axios.get(endpoint);
      setRender(response.data);
    } catch (err) {
      setError("Render not found");
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: render?.video_title,
          text: `Check out this Render digest: ${render?.video_title}`,
          url: url,
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !render) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-2xl text-primary mb-4">Render Not Found</h1>
          <p className="font-sans text-muted-foreground mb-6">This render may have been removed or the link is incorrect.</p>
          <Link to="/">
            <Button className="bg-primary text-primary-foreground">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="font-serif text-2xl font-medium text-primary">
              Render
            </Link>
            <Button
              data-testid="share-render-btn"
              variant="secondary"
              size="sm"
              onClick={handleShare}
              className="font-sans"
            >
              <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Share
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground/60 mb-4">
            {formatDate(render.created_at)}
          </p>
          <h1 className="font-serif text-3xl md:text-4xl font-medium text-primary mb-4">
            {render.video_title}
          </h1>
          <a
            href={render.video_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-sans text-sm text-accent hover:underline"
          >
            Watch on YouTube
            <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
          </a>
        </div>

        {/* Video Thumbnail */}
        {render.thumbnail_url && (
          <div className="flex justify-center mb-8">
            <a href={render.video_url} target="_blank" rel="noopener noreferrer">
              <img 
                src={render.thumbnail_url.replace('mqdefault', 'hqdefault')} 
                alt={render.video_title}
                className="rounded-lg shadow-md max-w-md w-full"
              />
            </a>
          </div>
        )}

        {/* Render the HTML content */}
        <div 
          className="prose prose-lg max-w-none"
          style={{
            fontFamily: 'Georgia, serif',
            lineHeight: '1.7',
            color: '#1A1A1A',
          }}
          dangerouslySetInnerHTML={{ 
            __html: render.generated_email_body_html
              .replace(/<body[^>]*>/, '<div>')
              .replace(/<\/body>/, '</div>')
              .replace(/<html[^>]*>/, '')
              .replace(/<\/html>/, '')
              .replace(/<head>[\s\S]*<\/head>/, '')
              .replace(/<!DOCTYPE[^>]*>/, '')
              // V14: Remove duplicate title, thumbnail, and header elements from email HTML
              .replace(/<h1[^>]*>[\s\S]*?<\/h1>/gi, '')
              .replace(/<div[^>]*text-align:\s*center[^>]*>[\s\S]*?<span[^>]*>Render<\/span>[\s\S]*?<\/div>/gi, '')
              .replace(/<div[^>]*text-align:\s*center[^>]*margin[^>]*>[\s\S]*?<a[^>]*>[\s\S]*?<img[^>]*>[\s\S]*?<\/a>[\s\S]*?<\/div>/gi, '')
          }} 
        />

        {/* CTA */}
        <div className="mt-12 pt-8 border-t border-border text-center">
          <p className="font-sans text-muted-foreground mb-4">
            Want daily video digests like this?
          </p>
          <Link to="/">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-sans">
              Try Render Free
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="font-sans text-sm text-muted-foreground">
            Powered by <Link to="/" className="text-accent hover:underline">Render</Link> — Your daily intellectual diet
          </p>
        </div>
      </footer>
    </div>
  );
};

export default RenderView;
